import { useEffect, useRef } from 'react';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CandidateNoteSpecialFields, {
  CandidateNoteSpecialFieldsType,
} from '@/components/CandidateNoteSpecialFields';
import TopicNote, { TopicNoteType } from '@/components/TopicNote';
import { onScrollToSmoothly } from '@/utils/global';
import { CandidateNotesCardProps } from './CandidateNotesCard.container';
import * as Styles from './CandidateNotesCard.styles';
import Menu from '../Menu';

export type CandidateNotesCardComponentProps = CandidateNoteSpecialFieldsType &
  TopicNoteType &
  Omit<CandidateNotesCardProps, 'onAddNewNote' | 'onChangeCandidateField'> & {
    newTopicNote: string;
    userName: string;
    onSelectTopicToDelete: (id: string) => void;
    onOpenEditModal: () => void;
  };

const CandidateNotesCardComponent = function ({
  name,
  userName,
  id,
  onChangeHandler,
  onSaveNote,
  notes,
  newTopicNote,
  candidate,
  onEditNote,
  onChangeSpecialFields,
  onUpdateNote,
  onDeleteNote,
  onCancelEditingNote,
  isSubmiting,
  onSelectTopicToDelete,
  onOpenEditModal,
  isDefault,
  onSaveSpecialFields,
  autoScroll,
  onAutoScroll,
}: CandidateNotesCardComponentProps): JSX.Element {
  const formattedLink = name.replace(/\s/g, '-').toLowerCase();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (formattedLink && ref.current && autoScroll) {
      onScrollToSmoothly(formattedLink);
      onAutoScroll?.();
    }
  }, [formattedLink, ref, autoScroll, onAutoScroll]);

  return (
    <Styles.TopicCard id={formattedLink} ref={ref}>
      <Styles.CardHeader>
        <Styles.TopicTitle>{name}</Styles.TopicTitle>
        {!isDefault && (
          <Menu
            icon={MoreHorizIcon}
            menuId={`${formattedLink}-id`}
            menuItems={[
              {
                label: 'Edit',
                menuItemIcon: CreateIcon,
                callback: onOpenEditModal,
              },
              {
                label: 'Delete',
                menuItemOptions: { warning: true },
                menuItemIcon: DeleteIcon,
                callback: (event) => {
                  event.stopPropagation();
                  onSelectTopicToDelete(id);
                },
              },
            ]}
          />
        )}
      </Styles.CardHeader>
      <CandidateNoteSpecialFields
        onSaveSpecialFields={onSaveSpecialFields}
        onChangeSpecialFields={onChangeSpecialFields}
        name={name}
        candidate={candidate}
      />
      <div>
        {notes.map((note, index) => {
          return (
            <TopicNote
              isSubmiting={isSubmiting}
              key={note.id}
              onCancelEditingNote={() => onCancelEditingNote(index)}
              onDeleteNote={() => onDeleteNote(index)}
              onSaveNote={() => onSaveNote(index)}
              onChangeHandler={onChangeHandler}
              onUpdateNote={(event) => onUpdateNote && onUpdateNote(event, index)}
              onEditNote={() => onEditNote && onEditNote(index)}
              {...note}
            />
          );
        })}

        <TopicNote
          isNewInput
          isSubmiting={isSubmiting}
          author={{ name: userName }}
          onSaveNote={onSaveNote}
          onCancelEditingNote={onCancelEditingNote}
          onDeleteNote={() => {}}
          onEditNote={() => {}}
          onUpdateNote={(event) => onUpdateNote && onUpdateNote(event)}
          onChangeHandler={onChangeHandler}
          text={newTopicNote}
        />
      </div>
    </Styles.TopicCard>
  );
};

export default CandidateNotesCardComponent;
